import { useState } from "react";
import Sidebar from "@/components/Sidebar";
import { Outlet, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Menu, LogOut } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import TrialBanner from "@/components/TrialBanner";

export default function AppLayout() {
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (loading) {
    return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}><div className="label">Loading…</div></div>;
  }
  if (!user) return <Navigate to="/app/login" replace />;
  // Wrong-shell guard: a student who accidentally lands on /app should be
  // bounced to their mobile app (typical vector: PWA start_url on a shared
  // device). Owners/coaches see the owner shell as expected.
  if (user.role === "student") return <Navigate to="/student" replace />;

  return (
    <div className="flex" style={{ background: "var(--bg)", minHeight: "100vh" }}>
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <Sidebar />
      </div>
      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40 flex" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={()=>setOpen(false)}/>
          <div className="relative z-10"><Sidebar onClose={()=>setOpen(false)}/></div>
        </div>
      )}
      <main className="flex-1 min-w-0" data-testid="app-main">
        <ImpersonationBanner />
        <TrialBanner />
        <div className="md:hidden flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}>
          <button onClick={()=>setOpen(true)} data-testid="mobile-menu-btn"><Menu size={22}/></button>
          <span className="font-bold tracking-tight">Roll<span style={{color:"var(--green)"}}>OSS</span></span>
          <button onClick={logout} data-testid="mobile-logout-btn" title="Sign out"
                  style={{ color: "var(--text-dim)" }}>
            <LogOut size={20}/>
          </button>
        </div>
        <ErrorBoundary><Outlet /></ErrorBoundary>
      </main>
    </div>
  );
}

export function PageHeader({ section, title, right }) {
  const { user } = useAuth();
  return (
    <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 px-5 md:px-8 py-5 md:py-6 border-b" style={{ borderColor: "var(--border)" }}>
      <div>
        <div className="label" style={{ color: "var(--green)" }}>{section}</div>
        <h1 className="text-[22px] md:text-[26px] font-bold tracking-tight mt-1 uppercase">{title}</h1>
      </div>
      <div className="flex items-center gap-4">
        {right}
        <div className="flex items-center gap-2">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 rounded-full" style={{ background: "var(--green)" }} />
          )}
          <div className="hidden sm:block">
            <div className="text-[13px] font-semibold">{user?.name || "Owner"}</div>
            <div className="label-tiny">{user?.role || "Owner"}</div>
          </div>
        </div>
      </div>
    </header>
  );
}
